import React, { useEffect } from "react";
import Icon from "../../Icon";
import { SEARCH_MODES } from "../constants";
import { useHoverDelay } from "../hooks";
import {
  ModeIconWrapper,
  ModeIconCircle,
  ModeMenu,
  ModeMenuItem
} from "./styles";

const ModeSwitcher = ({
  mode,
  modeIcon,
  onModeChange,
  mobile,
  forceClosed,
  outsideClickRef,
  canSwitch = true
}) => {
  const { isOpen, open, scheduleClose, close } = useHoverDelay();

  const otherMode =
    mode === SEARCH_MODES.SEARCH ? SEARCH_MODES.SIMILARITY : SEARCH_MODES.SEARCH;
  const otherModeIcon =
    otherMode === SEARCH_MODES.SEARCH ? "search" : "similar_tracks";

  useEffect(() => {
    if (forceClosed && isOpen) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClosed, isOpen]);

  // Support dismiss for mobile
  useEffect(() => {
    if (!isOpen || !mobile) return undefined;

    const handleDocClick = e => {
      const container = outsideClickRef?.current;
      if (container && !container.contains(e.target)) close();
    };

    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("touchstart", handleDocClick);

    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("touchstart", handleDocClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mobile, outsideClickRef]);


  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKey = e => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);

    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSwitch = () => {
    onModeChange(otherMode);
    close();
  };

  const handleCircleClick = e => {
    if (mobile) {
      if (isOpen) close();
      else open();
    } else {
      e.preventDefault();
    }
  };

  return (
    <ModeIconWrapper
      onMouseEnter={mobile || !canSwitch ? undefined : open}
      onMouseLeave={mobile || !canSwitch ? undefined : scheduleClose}
      aria-haspopup={canSwitch ? "menu" : undefined}
      aria-expanded={canSwitch ? isOpen : undefined}
    >
      <ModeIconCircle
        type="button"
        $mobile={mobile}
        $active={isOpen}
        onClick={canSwitch ? handleCircleClick : undefined}
        tabIndex={mobile && canSwitch ? undefined : -1}
        aria-label={
          canSwitch ? `Current search mode: ${mode}` : "Search"
        }
      >
        <Icon type={modeIcon} size="sm" inheritColor hoverBackground={false} />
      </ModeIconCircle>
      {canSwitch && isOpen && (
        <ModeMenu role="menu">
          <ModeMenuItem
            role="menuitem"
            type="button"
            onClick={handleSwitch}
            aria-label={`Switch to ${otherMode} mode`}
          >
            <Icon
              type={otherModeIcon}
              size="sm"
              inheritColor
              hoverBackground={false}
            />
          </ModeMenuItem>
        </ModeMenu>
      )}
    </ModeIconWrapper>
  );
};

export default ModeSwitcher;
